import React, { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Modal, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { type Dayjs } from 'dayjs';
import {
  useApporteursApi, type TCelluleTableau, type TDetailMois, type TLigneTableau,
} from '../../../api/paiement/apporteurs';
import ListeEnfants from '../../apporteur/ListeEnfants';

// Tableau animateur x mois (premium, freemium) pour la vérification papier
// mensuelle. Calculé en direct par le serveur ; une cellule qu'une correction
// enregistrée APRÈS la fin du mois a modifiée est signalée « modifié après
// coup » : son chiffre diffère de celui qu'on a pu relever à l'époque.

const TableauMensuel: React.FC = () => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [periode, setPeriode] = useState<[Dayjs, Dayjs]>([dayjs().subtract(5, 'month'), dayjs()]);
  const [mois, setMois] = useState<string[]>([]);
  const [lignes, setLignes] = useState<TLigneTableau[]>([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<{ ligne: TLigneTableau; mois: string } | null>(null);
  const [contenuDetail, setContenuDetail] = useState<TDetailMois | null>(null);
  const [chargementDetail, setChargementDetail] = useState(false);

  useEffect(() => {
    let vivant = true;
    setLoading(true);
    apiRef.current
      .tableau(periode[0].format('YYYY-MM'), periode[1].format('YYYY-MM'))
      .then((r) => {
        if (!vivant) return;
        setMois(r.mois);
        setLignes(r.lignes);
      })
      .catch((err) => {
        if (vivant) {
          message.error(err?.response?.data?.error || t('apporteurs.tableau.erreur', 'Échec du chargement du tableau'));
        }
      })
      .finally(() => vivant && setLoading(false));
    return () => {
      vivant = false;
    };
  }, [periode, t]);

  useEffect(() => {
    if (!detail) {
      setContenuDetail(null);
      return;
    }
    let vivant = true;
    setChargementDetail(true);
    apiRef.current
      .moisAdmin(detail.ligne.apporteur_id, detail.mois)
      .then((d) => vivant && setContenuDetail(d))
      .catch(() => vivant && message.error(t('apporteurs.tableau.detailErreur', 'Échec du chargement du détail')))
      .finally(() => vivant && setChargementDetail(false));
    return () => {
      vivant = false;
    };
  }, [detail, t]);

  const colonnes: ColumnsType<TLigneTableau> = [
    {
      title: t('apporteurs.admin.animateur', 'Animateur'),
      key: 'animateur',
      fixed: 'left',
      render: (_, l) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{[l.prenom, l.nom].filter(Boolean).join(' ') || `#${l.apporteur_id}`}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {l.code}
            {!l.actif && ` · ${t('apporteur.codeInactif', 'Inactif')}`}
          </Typography.Text>
        </Space>
      ),
    },
    ...mois.map((m, i) => ({
      title: dayjs(`${m}-01`).format('MM/YYYY'),
      key: m,
      align: 'center' as const,
      render: (_: unknown, l: TLigneTableau) => {
        const c: TCelluleTableau | undefined = l.cellules[i];
        if (!c) return '—';
        return (
          <Button type="link" onClick={() => setDetail({ ligne: l, mois: m })} style={{ padding: 0, height: 'auto' }}>
            <Space direction="vertical" size={0}>
              <span>
                <Tag color="gold" style={{ marginInlineEnd: 4 }}>{c.premium}</Tag>
                <Tag>{c.freemium}</Tag>
              </span>
              {c.modifie_apres_coup && (
                <Tooltip
                  title={t(
                    'apporteurs.tableau.modifieApresCoupAide',
                    "Une correction enregistrée après la fin du mois a changé ce chiffre."
                  )}
                >
                  <Tag color="orange" icon={<WarningOutlined />}>
                    {t('apporteurs.tableau.modifieApresCoup', 'modifié après coup')}
                  </Tag>
                </Tooltip>
              )}
            </Space>
          </Button>
        );
      },
    })),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <DatePicker.RangePicker
          picker="month"
          allowClear={false}
          value={periode}
          onChange={(v) => v && v[0] && v[1] && setPeriode([v[0], v[1]])}
          disabledDate={(d) => d.isAfter(dayjs(), 'month')}
        />
        <Typography.Text type="secondary">
          <Tag color="gold">{t('apporteur.premium', 'Premium')}</Tag>
          <Tag>{t('apporteur.freemium', 'Freemium')}</Tag>
          {t('apporteurs.tableau.legende', "Statut au dernier jour de chaque mois ; le mois en cours, aujourd'hui.")}
        </Typography.Text>
      </Space>
      <Table rowKey="apporteur_id" columns={colonnes} dataSource={lignes} loading={loading} scroll={{ x: true }} pagination={false} />
      <Modal
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={900}
        title={
          detail
            ? t('apporteurs.tableau.detailTitre', '{{animateur}} — {{mois}}', {
                animateur: [detail.ligne.prenom, detail.ligne.nom].filter(Boolean).join(' '),
                mois: dayjs(`${detail.mois}-01`).format('MM/YYYY'),
              })
            : ''
        }
      >
        {contenuDetail && (
          <Typography.Paragraph>
            <Tag color="gold">{contenuDetail.premium}</Tag> {t('apporteur.premium', 'Premium')}{' '}
            <Tag>{contenuDetail.freemium}</Tag> {t('apporteur.freemium', 'Freemium')}
          </Typography.Paragraph>
        )}
        <ListeEnfants enfants={contenuDetail?.enfants ?? []} loading={chargementDetail} />
      </Modal>
    </Space>
  );
};

export default TableauMensuel;
